import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { UserEntity } from './user.entity';

/**
 * Per-user, per-EVENT-TYPE notification channel preferences (PD-14290).
 * One row per (user, notificationType) carrying a boolean per delivery
 * channel — the grain the preferences matrix (PD-14292) edits and the
 * dispatcher (NotificationDispatchService) consults at send time.
 *
 * The grain is per notificationType (NOT per category) so each event is
 * individually controllable, even when several events share one pipeline
 * (client / opportunity / sourcing-request all flow through the watcher
 * handler but each gets its own row + toggles). `notificationType`
 * matches the values in NOTIFICATION_TYPE / the NOTIFICATION_EVENTS
 * catalog (free-form string at the DB layer — no migration to add types).
 *
 * Absence of a row = fall back to the event's catalog default (resolved
 * in NotificationPreferenceResolutionService), so existing users keep
 * today's behavior until they explicitly change something.
 *
 * Distinct from `user_notification_setting` (per-user master pause-all)
 * and from `client.notificationPreferences` (the unrelated CUSTOMER-facing
 * per-client model). This table is internal CRM users only.
 */
@Index(
	'user_notification_preference_user_type_idx',
	['fkUserId', 'notificationType'],
	{ unique: true },
)
@Entity('user_notification_preference')
export class UserNotificationPreferenceEntity extends BaseEntity {
	@Column('int', { name: 'fk_user__id', unsigned: true })
	public fkUserId!: number;

	@Column('varchar', { name: 'notificationType', length: 64 })
	public notificationType!: string;

	/** Show this event's notifications in the in-app bell tray. */
	@Column('tinyint', {
		name: 'inAppEnabled',
		width: 1,
		default: () => "'1'",
	})
	public inAppEnabled!: boolean;

	/** Send this event's notifications by email. */
	@Column('tinyint', {
		name: 'emailEnabled',
		width: 1,
		default: () => "'1'",
	})
	public emailEnabled!: boolean;

	/** Send this event's notifications as a push (browser / mobile). */
	@Column('tinyint', {
		name: 'pushEnabled',
		width: 1,
		default: () => "'0'",
	})
	public pushEnabled!: boolean;

	@ManyToOne(() => UserEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_user__id', referencedColumnName: 'id' }])
	public fkUser!: UserEntity;
}
